/**
 * Utility functions for the Lagewerkzeug frontend
 */

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with clsx
 * Handles conditional classes and resolves conflicts
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

interface FormatNumberOptions {
  decimals?: number
  fallback?: string
}

/**
 * Format a number with German locale (thousand separators, decimal comma)
 * @param value - The number to format (accepts unknown for table cell usage)
 * @param options - Number of decimal places OR options object { decimals, fallback }
 * @returns Formatted string or fallback if null/undefined/non-numeric
 */
export function formatNumber(
  value: unknown,
  options: number | FormatNumberOptions = 0
): string {
  const decimals = typeof options === 'number' ? options : (options.decimals ?? 0)
  const fallback = typeof options === 'number' ? 'k.A.' : (options.fallback ?? 'k.A.')

  if (value === null || value === undefined) return fallback

  const num = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(num)) return fallback

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

/**
 * Format an area value in km²
 * @param value - The area in km² (accepts unknown for table cell usage)
 * @returns Formatted string like "123,45 km²" or 'k.A.'
 */
export function formatArea(value: unknown): string {
  if (value === null || value === undefined) return 'k.A.'

  const num = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(num)) return 'k.A.'

  return `${formatNumber(num, 2)} km²`
}

/**
 * Format a data value for display in data source sections.
 * Handles null, boolean, string, and number types with German locale formatting.
 */
export function formatDataValue(
  key: string,
  value: unknown,
  options?: { fallback?: string; isIntegerField?: (key: string) => boolean }
): string {
  const fallback = options?.fallback ?? 'k.A.'
  if (value === null || value === undefined) return fallback
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein'
  if (typeof value === 'string') return value

  if (typeof value === 'number') {
    const isInteger = options?.isIntegerField?.(key) ?? false
    const decimals = isInteger ? 0 : 1
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: isInteger ? 0 : 2,
    }).format(value)
  }

  return String(value)
}

/**
 * Format an indicator value with optional unit for display.
 */
export function formatIndicatorValue(value: number | null, unit: string | null): string {
  if (value === null) return 'k.A.'

  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value)

  if (unit) {
    return `${formatted} ${unit}`
  }
  return formatted
}
